import { type FormEvent, useState } from 'react';
import type { ProfileData } from '../MyAccount.types';
import { postAccountAction } from '../shared/postAccountAction';

interface ProfilePageProps {
  data: ProfileData;
}

type FieldErrors = Record<string, string>;

export const ProfilePage = ({ data }: ProfilePageProps) => {
  const [firstName, setFirstName] = useState(data.firstName);
  const [lastName, setLastName] = useState(data.lastName);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [profileMessage, setProfileMessage] = useState<{ text: string; isError: boolean } | null>(null);
  const [passwordMessage, setPasswordMessage] = useState<{ text: string; isError: boolean } | null>(null);
  const [profileErrors, setProfileErrors] = useState<FieldErrors>({});
  const [passwordErrors, setPasswordErrors] = useState<FieldErrors>({});
  const [isPending, setIsPending] = useState(false);

  const postAction = async (url: string, body: Record<string, unknown>) => {
    setIsPending(true);
    try {
      return await postAccountAction(url, body);
    } finally {
      setIsPending(false);
    }
  };

  const clearFieldError = (setter: typeof setProfileErrors, field: string) => {
    setter(prev => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const handleUpdateProfile = async (e: FormEvent) => {
    e.preventDefault();
    setProfileMessage(null);
    setProfileErrors({});
    const result = await postAction(data.endpoints.updateAccount, {
      firstName,
      lastName,
      email: data.email,
    });
    if (!result.success && result.errors) {
      setProfileErrors(result.errors);
    }
    setProfileMessage({
      text: result.message ?? (result.success ? 'Profile updated successfully' : 'Failed to update profile'),
      isError: !result.success,
    });
  };

  const handleUpdatePassword = async (e: FormEvent) => {
    e.preventDefault();
    setPasswordMessage(null);
    setPasswordErrors({});
    const result = await postAction(data.endpoints.updatePassword, {
      currentPassword,
      newPassword,
    });
    if (result.success) {
      setCurrentPassword('');
      setNewPassword('');
    }
    if (!result.success && result.errors) {
      setPasswordErrors(result.errors);
    }
    setPasswordMessage({
      text: result.message ?? (result.success ? 'Password updated successfully' : 'Failed to update password'),
      isError: !result.success,
    });
  };

  return (
    <div className="prof-main">
      {/* Your details */}
      <div className="prof-section">
        <div className="ma-head"><h3>Your details</h3></div>
        <p className="ma-hint">Update the name we use across your account and order confirmations.</p>
        <div className="ma-card">
          <form onSubmit={handleUpdateProfile}>
            <div className="ma-field">
              <label className="ma-label" htmlFor="prof-first">First name</label>
              <input id="prof-first" className={`ma-input${profileErrors.firstName ? ' has-error' : ''}`} type="text" value={firstName} onChange={e => { setFirstName(e.target.value); clearFieldError(setProfileErrors, 'firstName'); }} required />
              {profileErrors.firstName && <span className="ma-field-error">{profileErrors.firstName}</span>}
            </div>
            <div className="ma-field">
              <label className="ma-label" htmlFor="prof-last">Last name</label>
              <input id="prof-last" className={`ma-input${profileErrors.lastName ? ' has-error' : ''}`} type="text" value={lastName} onChange={e => { setLastName(e.target.value); clearFieldError(setProfileErrors, 'lastName'); }} required />
              {profileErrors.lastName && <span className="ma-field-error">{profileErrors.lastName}</span>}
            </div>
            <div className="ma-field">
              <label className="ma-label" htmlFor="prof-email">Email</label>
              <input id="prof-email" className="ma-input" type="email" value={data.email} readOnly />
              <p className="ma-help">Your email is used to sign in and cannot be changed.</p>
            </div>
            <button type="submit" className="ma-btn-primary" disabled={isPending}>Update your details</button>
            {profileMessage && (
              <p className={profileMessage.isError ? 'ma-error' : 'ma-success'}>{profileMessage.text}</p>
            )}
          </form>
        </div>
      </div>

      {/* Your password */}
      <div className="prof-section">
        <div className="ma-head"><h3>Your password</h3></div>
        <p className="ma-hint">Choose a strong password you don&rsquo;t use anywhere else.</p>
        <div className="ma-card">
          <form onSubmit={handleUpdatePassword}>
            <div className="ma-field">
              <label className="ma-label" htmlFor="prof-cur-pw">Current password</label>
              <input id="prof-cur-pw" className={`ma-input${passwordErrors.currentPassword ? ' has-error' : ''}`} type="password" value={currentPassword} onChange={e => { setCurrentPassword(e.target.value); clearFieldError(setPasswordErrors, 'currentPassword'); }} required autoComplete="current-password" />
              {passwordErrors.currentPassword && <span className="ma-field-error">{passwordErrors.currentPassword}</span>}
            </div>
            <div className="ma-field">
              <label className="ma-label" htmlFor="prof-new-pw">New password</label>
              <input id="prof-new-pw" className={`ma-input${passwordErrors.newPassword ? ' has-error' : ''}`} type="password" value={newPassword} onChange={e => { setNewPassword(e.target.value); clearFieldError(setPasswordErrors, 'newPassword'); }} required autoComplete="new-password" />
              {passwordErrors.newPassword && <span className="ma-field-error">{passwordErrors.newPassword}</span>}
            </div>
            <button type="submit" className="ma-btn-primary" disabled={isPending}>Update your password</button>
            {passwordMessage && (
              <p className={passwordMessage.isError ? 'ma-error' : 'ma-success'}>{passwordMessage.text}</p>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};
